import { GetQueryParams, GetQueryResponse } from '../../../types/api.type';
import { Course } from '../../../types/course.type';
import { LedgerParams, LedgerResponse } from '../../../types/place.types';
import { sfcApi } from '../../api';

export interface AddCourseFormValues {
  id?: number;
  title: string;
  is_online: boolean;
  video_url?: string;
  payment_type: string;
  price_per_class: string;
  can_be_prorated?: boolean;
  course_type: string;
  theme_id: string;
  status: string;
  venue_id: string;
  is_hidden: string;
  opts_out_of_roster_emails: string;
  room?: string;
  capacity: string;
  instructor_ids: string[];
  min_instructors: string;
  instructor_notes?: string;
  pay_type: string;
  flat_rate?: string;
  admin_notes?: string;
  description: string;
  registered_information: string;
  day_of_week: string;
  starts_at: string;
  ends_at: string;
  starts_on?: string;
  ends_on?: string;
  no_class?: string;
  registration_starts_on?: string;
  registration_starts_at?: string;
  registration_ends_on?: string;
  registration_ends_at?: string;
  classes: any[];
  files?: File[];
  file_metadata?: any[];
  document_ids?: string[];
}

export interface UpdateEmailsValues {
  id?: number;
  id_course: number;
  emails: string;
}
export interface CoursesEmails {
  id?: number;
  id_course: number;
  emails: string;
  course: Course;
}

export interface CoursesEmailSend {
  id: number;
  type: string;
}

export const courseApi = sfcApi.injectEndpoints({
  endpoints: (builder) => ({
    getCourses: builder.query<GetQueryResponse, GetQueryParams | void>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.scope) queryParams.set('scope', params.scope);
        if (params?.omitScopeCount) queryParams.set('omit_scope_count', 'true');
        const qs = queryParams.toString();

        return {
          url: `courses${qs ? `?${qs}` : ''}`,
          method: 'POST',
          body: params?.data
        };
      },
      providesTags: ['Course']
    }),
    searchCourses: builder.query<GetQueryResponse, (GetQueryParams & { scopes: string[] }) | void>({
      query: (params) => ({
        url: `courses/search?omit_scope_count=true&scope[]=${params?.scopes.join('&scope[]=')}`,
        method: 'POST',
        body: params?.data
      }),
      providesTags: ['Course']
    }),
    getCoursesSimple: builder.query<GetQueryResponse, GetQueryParams | void>({
      query: (params) => ({
        url: `courses/simple${params?.scope ? `?scope=${params.scope}` : ''}`,
        method: 'POST',
        body: params?.data
      })
    }),
    getCourse: builder.query<{ course: Course }, number>({
      query: (id) => `courses/${id}`,
      providesTags: (result, error, id) => [{ type: 'Course', id }]
    }),
    getCoursePublic: builder.query<{ course: Course }, string>({
      query: (id) => `courses/public/${id}`,
      providesTags: (result, error, id) => [{ type: 'Course', id }]
    }),
    createCourse: builder.mutation<{ course: Course }, AddCourseFormValues | FormData>({
      query: (data) => ({
        url: 'courses/create',
        method: 'POST',
        body: data,
        headers: data instanceof FormData ? {} : { 'Content-Type': 'application/json' }
      }),
      invalidatesTags: ['Course']
    }),
    updateCourse: builder.mutation<{ course: Course }, AddCourseFormValues | FormData>({
      query: (data) => {
        let id: number | undefined;
        let body: FormData | AddCourseFormValues;

        if (data instanceof FormData) {
          id = Number(data.get('id'));
          data.delete('id'); // Remove id from FormData
          body = data;
        } else {
          id = data.id;
          body = { ...data };
          delete body.id;
        }

        return {
          url: `courses/update/${id}`,
          method: 'PUT',
          body: body,
          headers: body instanceof FormData ? {} : { 'Content-Type': 'application/json' }
        };
      },
      invalidatesTags: (result) => [{ type: 'Course', id: result?.course.id }, 'Course']
    }),

    getFavoriteCourse: builder.query<{ courseFavorited: boolean }, number>({
      query: (id) => `courses/favorite/${id}`
    }),
    toggleFavoriteCourse: builder.query<{ courseFavorited: boolean }, number>({
      query: (id) => `courses/toggleFavorite/${id}`
    }),
    getFavoriteCourses: builder.query<GetQueryResponse, GetQueryParams | void>({
      query: () => 'courses/favorites'
    }),
    getCourseLedger: builder.query<LedgerResponse, LedgerParams>({
      query: (params) => ({
        url: `courses/ledger/${params.id}`
      }),
      providesTags: ['CourseLedger']
    }),
    updateEmails: builder.mutation<{ updaterEmails: CoursesEmails }, UpdateEmailsValues>({
      query: (data) => {
        const { id_course, emails } = data;
        return {
          url: `/courses/update-emails/${id_course}`,
          method: 'PUT',
          body: { emails: emails }
        };
      },
      invalidatesTags: (result) => [
        { type: 'Course', id: result?.updaterEmails?.course?.id },
        'Course'
      ]
    }),
    sendTextReminder: builder.mutation<{ updater: CoursesEmailSend }, CoursesEmailSend>({
      query: (data) => {
        const { id, type } = data;
        return {
          url: `/courses/${type}/${id}`,
          method: 'GET'
        };
      },
      invalidatesTags: (result) => [{ type: 'Course', id: result?.updater?.id }, 'Course']
    }),
    sendWelcomeEmail: builder.mutation<string, { courseId: number; recipient?: string; bulk?: boolean }>({
      query: ({ courseId, recipient, bulk }) => {
        const params = new URLSearchParams();
        if (recipient) params.set('recipient', recipient);
        if (bulk) params.set('bulk', 'true');
        return {
          url: `/courses/welcome-course/${courseId}?${params.toString()}`,
          method: 'GET'
        };
      },
      invalidatesTags: ['Course']
    }),
    sendThankEmail: builder.mutation<string, { courseId: number; recipient?: string; bulk?: boolean }>({
      query: ({ courseId, recipient, bulk }) => {
        const params = new URLSearchParams();
        if (recipient) params.set('recipient', recipient);
        if (bulk) params.set('bulk', 'true');
        return {
          url: `/courses/thank-course/${courseId}?${params.toString()}`,
          method: 'GET'
        };
      },
      invalidatesTags: ['Course']
    }),
    sendReviewRequests: builder.mutation<{ sent: number; skipped: number; failed: number }, number>({
      query: (courseId) => ({
        url: `/courses/review-request/${courseId}`,
        method: 'POST'
      }),
      invalidatesTags: ['Course']
    })
  })
});

export const {
  useGetCoursesQuery,
  useSearchCoursesQuery,
  useGetCoursesSimpleQuery,
  useGetCourseQuery,
  useGetCoursePublicQuery,
  useCreateCourseMutation,
  useUpdateCourseMutation,
  useGetFavoriteCourseQuery,
  useLazyToggleFavoriteCourseQuery,
  useGetFavoriteCoursesQuery,
  useGetCourseLedgerQuery,
  useUpdateEmailsMutation,
  useSendTextReminderMutation,
  useSendWelcomeEmailMutation,
  useSendThankEmailMutation,
  useSendReviewRequestsMutation,
} = courseApi;